import {objectTransenum} from "#components/SForm/SFormElement/utils";
import { isNumber } from 'lodash'

export const financial = (x: string | number, forInput: boolean = false) => {
  if (forInput) {
    return `${x}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  if (isNaN(Number(x)) || x == undefined) {
    return typeof x === 'string' ? x : 0
  }
  x = "" + x
  return Number.parseFloat(x).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const financialToNumber = (x: string | number) => {
  if (typeof x === 'number') {
    return x;
  }

  if (typeof x !== 'string') {
    return null;
  }
  let newX = +(x.replace(/,/g, ''));
  if (isNaN(newX)) {
    return null;
  }
  return newX
}


// 枚举货币（key = 币种代码小写，即币种下拉选项源；rmb 为人民币历史别名）
export const currencyEnum = {
  rmb: '人民币',
  usd: '美元',
  eur: '欧元',
  gbp: '英镑',
  jpy: '日元',
  hkd: '港币',
  twd: '新台币',
  krw: '韩元',
  myr: "林吉特",
  sgd: '新加坡元',
  aud: '澳元',
}

export const currencyOptions = objectTransenum(currencyEnum);

/**
 * 币种代码 → 中文名反显（只读展示用）。
 * currencyEnum 的 key 即币种代码小写（rmb 为人民币历史别名）；CNY 是后端人民币标准代码
 * （CurrencyEnum.RMB.getStandardCode()），在此归一化到 rmb 键，保证 CNY 反显仍为中文。
 * 未知币种返回原代码兜底。
 */
export const getCurrencyName = (currency?: string | null): string => {
  if (!currency) return '';
  const key = currency.toLowerCase();
  if (key === 'cny') return currencyEnum.rmb;
  return currencyEnum[key] || currency;
};

/**
 * 前端币种 key（小写，rmb 为人民币别名）→ 后端 CurrencyEnum standardCode（大写，人民币标准代码 CNY）。
 * rmb → CNY 特判；其余小写转大写（usd→USD、hkd→HKD、eur→EUR …）。
 */
export const toCurrencyStandardCode = (currency?: string | null): string | undefined => {
  if (!currency) return undefined;
  const lower = currency.toLowerCase();
  if (lower === 'rmb') return 'CNY';
  return lower.toUpperCase();
};


// 添加千位符，保留小数，小数维护可以配置
export function addThousandSeparator(price: number, decimalPlaces: number = 2) {
  if (isNaN(+price)) {
    return '--';
  }
  return (+price)?.toFixed?.(decimalPlaces).replace(/\d(?=(\d{3})+\.)/g, '$&,');
}

export function removeThousandSeparator(price: string) {
  return price?.toString()?.replace(/,/g, '');
}

export function priceFormat(priceInfo: {
  price: number,
  currency: string,
}) {
  let { price, currency } = priceInfo;
  // 数据可能是字符串，且被格式化过，例如：1,000.00
  // 转成数字
  if (typeof price === 'string') {
    price = financialToNumber(price);
  }

  if (isNaN(+price)) {
    return '--'
  }
  // 千分位分隔符，保留两位小数，如果是整数，不显示小数
  const priceStr = (+price)?.toFixed?.(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
  // currency小写
  const currencyStr = currencyEnum[currency?.toLowerCase()] || currencyEnum.rmb;
  return `${priceStr} ${currencyStr}`;
}

/**
 * 千分位格式化
 *
 * 与 `financial` / `addThousandSeparator` 的区别：
 * - 不强制保留小数位，整数只展示整数部分（如 `1000` → `"1,000"`）
 * - 有小数时原样保留（如 `1000.5` → `"1,000.5"`，`1000.50` → `"1,000.50"`）
 * - 兼容 number / string / undefined / null / NaN，非法值返回 `''`
 * - 支持负数（如 `-1000.5` → `"-1,000.5"`）
 *
 * @param value - 数值或数值字符串（可含已有千分位逗号，会先清除）
 * @returns 千分位格式化后的字符串，非法值返回 `''`
 *
 * @example
 * formatThousands(1234567)         // "1,234,567"
 * formatThousands(1234567.89)      // "1,234,567.89"
 * formatThousands('1,234,567.89') // "1,234,567.89"（先清除旧逗号再格式化）
 * formatThousands(undefined)       // ""
 * formatThousands(NaN)             // ""
 */
export function formatThousands(value: number | string | undefined | null): string {
  if (value === undefined || value === null || value === '') return '';
  // 清除已有千分位逗号，避免二次格式化错误
  const cleaned = `${value}`.replace(/,/g, '');
  if (isNaN(Number(cleaned))) return '';
  const isNegative = cleaned.startsWith('-');
  const abs = isNegative ? cleaned.slice(1) : cleaned;
  const [intPart, decimalPart] = abs.split('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const result = decimalPart !== undefined ? `${formattedInt}.${decimalPart}` : formattedInt;
  return isNegative ? `-${result}` : result;
}

export function orderNum(orderIndex, index?) {
  if (isNumber(orderIndex) && !isNaN(orderIndex)) {
    return orderIndex + 1;
  }
  if (isNumber(index) && !isNaN(index)) {
    return index + 1;
  }
  return ''
}

/**
 * 把任意值安全转换成有限数字（fallback=0）。
 *
 * 主要用于交给 `decimal.js`：直接调用 `new Decimal(...)` 时，
 * 以下值都会抛 `[DecimalError] Invalid argument`，必须先规范化：
 *   - 空字符串 `''`、`'  '`（可编辑表格 InputNumber 清空时的常见运行时值）
 *   - `NaN` / `Infinity` / `-Infinity`
 *   - 非数字字符串（`'--'`、`'1,000'`、`'abc'` 等）
 *   - 对象、布尔值等非数值类型
 *
 * 兜底规则：
 *   - `null` / `undefined`              → `fallback`
 *   - `number` 且有限                    → 原值
 *   - `number` 是 NaN / Infinity         → `fallback`
 *   - 数字字符串（自动 trim）            → `Number(trim)`
 *   - 其它（空串 / 对象 / 布尔等）       → `fallback`
 *
 * 典型用法：
 * ```ts
 * import Decimal from 'decimal.js';
 * import { toFiniteNumber } from '@mpc/common/src/utils';
 *
 * // ❌ 不安全：record.amount 可能是 '' / NaN 导致抛错
 * new Decimal(record.amount ?? 0);
 *
 * // ✅ 安全：任何非法值都被规范成 0（或自定义 fallback）
 * new Decimal(toFiniteNumber(record.amount));
 * new Decimal(toFiniteNumber(record.amount, NaN)); // 想保留"非法即 NaN"的语义
 * ```
 *
 * @param v        待规范化的任意值
 * @param fallback 非法值时的回退值（默认 0；传 NaN 可用于上层做 isFinite 二次判定）
 */
export function toFiniteNumber(v: unknown, fallback = 0): number {
  if (v === null || v === undefined) return fallback;
  if (typeof v === 'number') return Number.isFinite(v) ? v : fallback;
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (trimmed === '') return fallback;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

/**
 * 格式化文件大小为可读字符串。
 *
 * @param bytes - 文件大小（字节）
 * @returns 格式化后的尺寸字符串（B / KB / MB / GB，保留一位小数）
 *
 * @example
 * formatFileSize(0)       // "0B"
 * formatFileSize(512)     // "512B"
 * formatFileSize(1536)    // "1.5KB"
 * formatFileSize(1048576) // "1.0MB"
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)}GB`;
}